<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { AlertCircle, Upload, X } from "@lucide/vue"
import { computed, ref, useId } from "vue"
import { cn } from "@/shared/lib/utils"
// Прямий імпорт із сегмента `button` (не з барелю @/shared/ui) — той самий шар,
// публічний sub-export; уникаємо циклічного index.ts (spec §4).
import { buttonVariants } from "@/shared/ui/button"

/**
 * DS-примітив FileInput (tech-069). «Дурний» презентаційний контрол: відкриває
 * системний file picker, емітить обраний File і відображає життєвий цикл upload
 * (status/progress/error), яким керує консюмер. Власного HTTP не має (spec §1/§5).
 */
const props = withDefaults(
  defineProps<{
    /** Whitelist для нативного `accept` діалогу. Advisory-only — НЕ безпека (spec §9). */
    accept?: string
    /** Контрольований життєвий цикл (консюмер-driven). */
    status?: "idle" | "uploading" | "error"
    /** 0–100 — визначений прогрес; 'indeterminate' (або omit) — невизначений. */
    progress?: number | "indeterminate"
    /** Повідомлення помилки при status="error" (консюмер мапить API-код у текст). */
    error?: string
    /** Блокує picker. Ортогонально до status. */
    disabled?: boolean
    /** Текст тригера. */
    label?: string
    /** Допоміжний підпис під контролом. */
    hint?: string
    /** id нативного input — для звʼязку із зовнішнім <Label for>. */
    id?: string
    class?: HTMLAttributes["class"]
  }>(),
  {
    status: "idle",
    progress: "indeterminate",
    disabled: false,
    label: "Обрати файл",
  },
)

const emit = defineEmits<{
  select: [file: File]
  clear: []
}>()

const inputRef = ref<HTMLInputElement | null>(null)
/** Echo обраного імені файлу (uncontrolled-display, spec §5.1). Не авторитетний стан. */
const selectedName = ref<string | null>(null)

const uid = useId()
const hintId = computed(() => (props.hint ? `${uid}-hint` : undefined))
const errorId = computed(() =>
  props.status === "error" && props.error ? `${uid}-error` : undefined,
)

const isError = computed(() => props.status === "error")
/** Тригер неінтерактивний під час upload (запобігає паралельному вибору, spec §12). */
const pickerDisabled = computed(() => props.disabled || props.status === "uploading")

/** X показуємо коли є обраний файл АБО триває upload (cancel); не в disabled. */
const showClear = computed(
  () => !props.disabled && (selectedName.value !== null || props.status === "uploading"),
)

const isIndeterminate = computed(
  () => typeof props.progress !== "number" || Number.isNaN(props.progress),
)
/** Клемп числового прогресу до 0–100 (spec §10). */
const clampedProgress = computed(() =>
  isIndeterminate.value
    ? undefined
    : Math.min(100, Math.max(0, props.progress as number)),
)

const describedBy = computed(() => {
  const ids = [hintId.value, errorId.value].filter(Boolean)
  return ids.length ? ids.join(" ") : undefined
})

function onChange(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  // Скидаємо value, щоб повторний вибір ТОГО САМОГО файлу знову тригерив change (spec §10).
  target.value = ""
  if (!file)
    return
  selectedName.value = file.name
  emit("select", file)
}

function onClear() {
  selectedName.value = null
  if (inputRef.value)
    inputRef.value.value = ""
  emit("clear")
}
</script>

<template>
  <div
    data-slot="file-input"
    :data-state="status"
    :data-disabled="disabled || undefined"
    :class="cn('flex w-full flex-col gap-2', props.class)"
  >
    <div class="flex items-center gap-2">
      <label
        data-slot="file-input-trigger"
        :class="
          cn(
            buttonVariants({ variant: 'outline' }),
            'cursor-pointer',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
          )
        "
        :aria-disabled="pickerDisabled || undefined"
      >
        <input
          :id="id"
          ref="inputRef"
          type="file"
          class="sr-only"
          :accept="accept"
          :disabled="pickerDisabled"
          :aria-invalid="isError || undefined"
          :aria-describedby="describedBy"
          @change="onChange"
        >
        <Upload
          class="size-4"
          aria-hidden="true"
        />
        <span>{{ label }}</span>
      </label>

      <span
        v-if="selectedName"
        class="min-w-0 flex-1 truncate text-sm text-muted-foreground"
        :title="selectedName"
      >{{ selectedName }}</span>

      <button
        v-if="showClear"
        type="button"
        data-slot="file-input-clear"
        :class="cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }))"
        :aria-label="status === 'uploading' ? 'Скасувати завантаження' : 'Прибрати файл'"
        @click="onClear"
      >
        <X
          class="size-4"
          aria-hidden="true"
        />
      </button>
    </div>

    <div
      v-if="status === 'uploading'"
      class="flex flex-col gap-1"
    >
      <div
        role="progressbar"
        aria-label="Прогрес завантаження"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="clampedProgress"
        :aria-busy="isIndeterminate || undefined"
        class="bg-muted h-1.5 w-full overflow-hidden rounded-full"
      >
        <div
          v-if="!isIndeterminate"
          class="bg-primary h-full rounded-full transition-[width]"
          :style="{ width: `${clampedProgress}%` }"
        />
        <div
          v-else
          class="bg-primary h-full w-1/3 animate-pulse rounded-full motion-reduce:animate-none"
        />
      </div>
      <p class="text-xs text-muted-foreground">
        {{ isIndeterminate ? "Завантаження…" : `Завантаження… ${clampedProgress}%` }}
      </p>
    </div>

    <p
      v-if="hint"
      :id="hintId"
      class="text-sm text-muted-foreground"
    >
      {{ hint }}
    </p>

    <div
      v-if="isError && error"
      :id="errorId"
      role="alert"
      aria-live="assertive"
      class="text-destructive flex items-center gap-1.5 text-sm"
    >
      <AlertCircle
        class="size-4 shrink-0"
        aria-hidden="true"
      />
      <span>{{ error }}</span>
    </div>
  </div>
</template>
